import { useEventCallback } from '@mui/material';
import { DateTime } from 'luxon';
import { useState, useMemo, useEffect } from 'react';
import { useUserProfile } from '../use-user-profile';

type OTPVM = {
  isValid: boolean;
  expirationTime?: DateTime | null;
  value: string;
  setValidity: (validity: boolean) => void;
};

export const hourValidity = 4;

/**
 * Hook per controllare la validità dell'OTP.
 * @param {number} [checkTime=600000] - tempo di scadenza per controllare la validità dell'OTP
 * @returns {OTPVM} - oggetto contenente le seguenti proprietà:
 *   isValid: boolean - indica se l'OTP è valido o meno
 *   expirationTime?: DateTime | null - indica il tempo di scadenza dell'OTP
 *   value: string - OTP code
 *   setValidity: (validity: boolean) => void - metodo per controllare la validità dell'OTP
 */
export default function useOtp(checkTime = 600000): OTPVM {
  const [otpIsValid, setIsValid] = useState<boolean>(false);

  const { userProfile } = useUserProfile();

  const expirationDateOTP = useMemo(() => {
    let timestampOtpCode = userProfile?.datiLogin?.currentTimestamp || '';

    if (typeof timestampOtpCode === 'number') {
      timestampOtpCode = DateTime.fromMillis(timestampOtpCode).toISO() || '';
    }
    const creationTimestampOtpCode = timestampOtpCode
      ? DateTime.fromISO(timestampOtpCode)
      : undefined;

    return creationTimestampOtpCode
      ? creationTimestampOtpCode.plus({ hour: hourValidity })
      : undefined;
  }, [userProfile?.datiLogin?.currentTimestamp]);

  // controlla la validità dell'otp
  const controlOTP = useEventCallback(() => {
    const otpCode = userProfile.datiLogin?.otp || '';

    const isValid = otpCode && expirationDateOTP ? expirationDateOTP > DateTime.now() : false;

    setIsValid((oldState) => {
      if (isValid === oldState) {
        return oldState;
      }

      return isValid;
    });
  });

  useEffect(() => {
    controlOTP();
    // si controlla ogni 10 minuti
    setInterval(() => {
      controlOTP();
    }, checkTime);
  }, [userProfile.datiLogin?.otp]);

  return {
    isValid: otpIsValid,
    expirationTime: expirationDateOTP,
    value: userProfile.datiLogin?.otp || '',
    setValidity: setIsValid,
  };
}
